import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  Lead,
  LeadRequest,
  LeadSearchParams,
  LeadState,
  LeadCycle,
  LeadDisqualificationReason,
  PageResponse,
  LeadBulkActionRequest,
  LeadBulkActionResponse
} from '../models/lead.model';
import { CrmUserResponse } from '../../users/models/user.model';
import { FullHttpParameterCodec } from '../../../shared/http/full-parameter-codec';

@Injectable({
  providedIn: 'root'
})
export class LeadApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) { }

  // GET /api/leads - List leads with filters and pagination
  searchLeads(params: LeadSearchParams): Observable<PageResponse<Lead>> {
    let httpParams = new HttpParams({ encoder: new FullHttpParameterCodec() })
      .set('businessId', params.businessId.toString());

    if (params.phonePrefix) {
      httpParams = httpParams.set('phonePrefix', params.phonePrefix);
    }
    if (params.idContact !== undefined) {
      httpParams = httpParams.set('idContact', params.idContact.toString());
    }
    if (params.idCompany !== undefined) {
      httpParams = httpParams.set('idCompany', params.idCompany.toString());
    }
    if (params.idLeadCycle !== undefined) {
      httpParams = httpParams.set('idLeadCycle', params.idLeadCycle.toString());
    }
    if (params.idLeadState !== undefined) {
      httpParams = httpParams.set('idLeadState', params.idLeadState.toString());
    }
    if (params.isDisqualified !== undefined) {
      httpParams = httpParams.set('isDisqualified', params.isDisqualified.toString());
    }
    if (params.isRetryScheduled !== undefined) {
      httpParams = httpParams.set('isRetryScheduled', params.isRetryScheduled.toString());
    }
    if (params.searchName) {
      httpParams = httpParams.set('searchName', params.searchName);
    }
    if (params.userRegisterId !== undefined) {
      httpParams = httpParams.set('userRegisterId', params.userRegisterId.toString());
    }
    if (params.userPropietyId !== undefined) {
      httpParams = httpParams.set('userPropietyId', params.userPropietyId.toString());
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }
    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params.sortDirection) {
      httpParams = httpParams.set('sortDirection', params.sortDirection);
    }

    return this.http.get<PageResponse<Lead>>(`${this.apiUrl}/leads`, { params: httpParams });
  }

  // GET /api/leads/{id}?businessId=<id> - Get lead by ID
  getLeadById(id: number, businessId: number): Observable<Lead> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<Lead>(`${this.apiUrl}/leads/${id}`, { params });
  }

  // POST /api/leads - Create new lead
  createLead(lead: LeadRequest): Observable<Lead> {
    return this.http.post<Lead>(`${this.apiUrl}/leads`, lead);
  }

  // PUT /api/leads/{id} - Update existing lead
  // NOTE: idLeadCycle in the body is ignored by the backend — use changeCycle() instead
  updateLead(id: number, lead: LeadRequest): Observable<Lead> {
    return this.http.put<Lead>(`${this.apiUrl}/leads/${id}`, lead);
  }

  // POST /api/leads/{id}/change-cycle - Change lead cycle and execute lifecycle rules
  changeCycle(id: number, cycleId: number): Observable<Lead> {
    return this.http.post<Lead>(`${this.apiUrl}/leads/${id}/change-cycle`, { cycleId });
  }

  // DELETE /api/leads/{id}?businessId=<id> - Delete lead
  deleteLead(id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/leads/${id}`, { params });
  }

  // PATCH /api/leads/{id}/change-owner - Change lead owner
  changeOwner(id: number, userPropietyId: number): Observable<Lead> {
    return this.http.patch<Lead>(`${this.apiUrl}/leads/${id}/change-owner`, { userPropietyId });
  }

  // POST /api/leads/{id}/reopen?businessId=<id> - Reactivar un lead descalificado.
  // El BACKEND mueve el ciclo a "Reintentando" y deja condicion="Activo".
  // NO usar change-cycle manual para reabrir (Inc 3/37/38).
  reopenLead(id: number, businessId: number): Observable<Lead> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<Lead>(`${this.apiUrl}/leads/${id}/reopen`, null, { params });
  }

  // PATCH /api/leads/{id}/disqualify - Disqualify a lead
  disqualifyLead(id: number, businessId: number, disqualificationReasonId: number, disqualificationNotes?: string, retryDate?: string, isRetryScheduled?: boolean): Observable<Lead> {
    let params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('disqualificationReasonId', disqualificationReasonId.toString());
    if (disqualificationNotes) {
      params = params.set('disqualificationNotes', disqualificationNotes);
    }
    if (retryDate) {
      params = params.set('retryDate', retryDate);
    }
    if (isRetryScheduled !== undefined) {
      params = params.set('isRetryScheduled', isRetryScheduled.toString());
    }
    return this.http.patch<Lead>(`${this.apiUrl}/leads/${id}/disqualify`, null, { params });
  }

  // GET /api/lead-states?businessId=<id>[&activeOnly=true] - List lead states
  // activeOnly=true filters out inactive states. Use in lead form/kanban so the
  // user cannot pick an inactive state. List of states pages omit it to keep
  // showing inactive records for management.
  getLeadStates(businessId: number, activeOnly = false): Observable<LeadState[]> {
    let params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '100');
    if (activeOnly) params = params.set('activeOnly', 'true');
    return this.http.get<PageResponse<LeadState>>(`${this.apiUrl}/lead-states`, { params })
      .pipe(map(res => res.content));
  }

  // GET /api/lead-cycles?businessId=<id>[&activeOnly=true] - List lead cycles
  getLeadCycles(businessId: number, activeOnly = false): Observable<LeadCycle[]> {
    let params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '100');
    if (activeOnly) params = params.set('activeOnly', 'true');
    return this.http.get<PageResponse<LeadCycle>>(`${this.apiUrl}/lead-cycles`, { params })
      .pipe(map(res => res.content));
  }

  // GET /api/lead-disqualification-reasons?businessId=<id>[&activeOnly=true]
  getDisqualificationReasons(businessId: number, activeOnly = false): Observable<LeadDisqualificationReason[]> {
    let params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('page', '0')
      .set('size', '100');
    if (activeOnly) params = params.set('activeOnly', 'true');
    return this.http.get<PageResponse<LeadDisqualificationReason>>(`${this.apiUrl}/lead-disqualification-reasons`, { params })
      .pipe(map(res => res.content));
  }

  // POST /api/leads/bulk-action - Acción masiva (CHANGE_OWNER | CHANGE_CYCLE | DELETE) sobre leads.
  // businessId/userId salen del token (no enviar). Los fallos por lead vienen en 200 dentro de failures[].
  bulkActionLeads(req: LeadBulkActionRequest): Observable<LeadBulkActionResponse> {
    return this.http.post<LeadBulkActionResponse>(`${this.apiUrl}/leads/bulk-action`, req);
  }

  // GET /api/auth/users/selectable?businessId - Usuarios activos del negocio (para el picker de responsable).
  // Endpoint sin permiso 'users:VIEW' (solo aislamiento por tenant): roles como Ventas pueden poblar
  // el selector. El listado completo /auth/users exige users:VIEW y devuelve 403 a esos roles.
  getUsersByBusiness(businessId: number): Observable<CrmUserResponse[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString());
    return this.http.get<CrmUserResponse[]>(`${this.apiUrl}/auth/users/selectable`, { params })
      .pipe(map(r => r ?? []));
  }
}
